import React, { ReactNode, useEffect, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Database, X } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { formatMoney, formatRate } from '../../lib/money';
import { cycleOffers, defaultCycle, monthlyPremium, yearlySaving } from '../../lib/billing-cycle';
import type { MonthlyPremium } from '../../lib/billing-cycle';
import type { Cadence, PickerOption } from '../../lib/plans';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useItemSeenBi } from '../../lib/use-item-seen-bi';
import type { AppliedCoupon } from '../../lib/coupon';
import { CouponField } from './CouponField';
import { Badge, Button, Card, SelectionIndicator, TextButton, Typography } from '../symphony/ui';
import { usePurchaseAttribution } from '../../lib/purchase-attribution-context';
import styles from './BillingCycleStep.module.css';

/**
 * The picker's SECOND screen: pick a billing cycle for a plan already chosen.
 *
 * This is the variant arm of `symphonyBillingCycleStepWeb`. Control keeps today's single
 * screen with a Monthly/Yearly toggle above the cards; here the toggle is gone and the cycle is asked
 * once, after the plan — so the first screen compares PLANS and this one compares PRICES. The bet is
 * that a toggle sitting above four cards makes the buyer answer both questions at once, and silently
 * re-prices every card each time they touch it.
 *
 * **This is not a desktop-only screen.** Both native apps send buyers to this very page
 * (`CreditsUpgrade.symphonyURL` -> symphony.wix.com/packages, on iOS and Android alike), so phone
 * width is a real surface here and the module's container query is what serves it. That is also why
 * there is nothing to port to `native-apps/`: the three surfaces share one picker by construction.
 *
 * Deliberately NOT a modal. It replaces the cards in the same pane, keeps the picker's own disclaimer
 * underneath it, and has a real Back control — the buyer is mid-purchase and must be able to change
 * their mind about the plan without losing the page.
 *
 * ## Expansion IS selection
 *
 * Exactly one row is open, and it is the one that will be bought. There is deliberately no second
 * state where a buyer has expanded monthly to read it while yearly is still armed: that arrangement
 * puts the details of one option directly above the Continue button of another, which is how a price
 * gets misread. The chevron is an affordance for that single act, not a separate control.
 */

export interface BillingCycleStepProps {
  /** The chosen plan's display name, already resolved through `t()` by the picker. */
  planName: string;
  /**
   * The card key (`basic` / `pro` / `max`) — the plan's name for BI, where `planName` cannot be used
   * because it is translated and would split one plan across 37 locales.
   */
  planKey: string;
  /** The chosen plan option (Max vs Max+ was settled on the cards). */
  option: PickerOption;
  /** ISO currency of the resolved prices, for the symbol. */
  currency?: string;
  /**
   * Catalog keys for the "What you get" lines. Passed in rather than read off `option.featureKeys` so
   * both screens list the same benefits for the same plan — the cards resolve them the same way, and
   * a second source here would be free to drift.
   */
  featureKeys: string[];
  /**
   * True when the buyer already holds a real paid subscription, so Continue opens the in-place
   * plan-switch confirmation instead of Stripe Checkout. The variant removes the cadence toggle for
   * EVERYONE, so a subscriber reaches this screen too — without it they would have no way to choose a
   * cycle at all.
   */
  isSwitch: boolean;
  /** A purchase is already in flight — the CTA is held so a second click can't mint a second session. */
  busy?: boolean;
  /**
   * Whether promo codes are on for this buyer (the live billing lane). In the control arm the field
   * sits on each plan card; in this arm it MOVES HERE, and that is a correctness fix rather than a
   * relocation — see the block comment above the field below.
   */
  couponsEnabled: boolean;
  /** Buy at this cadence, with the promo code the buyer applied to this exact target. */
  onContinue: (cadence: Cadence, couponCode?: string) => void;
  /** Back to the cards. */
  onBack: () => void;
}

export function BillingCycleStep({
  planName,
  planKey,
  option,
  currency,
  featureKeys,
  isSwitch,
  busy = false,
  couponsEnabled,
  onContinue,
  onBack,
}: BillingCycleStepProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  // The arm's impression. Measured off the viewport rather than mount, because the conversion rate
  // this experiment exists to compare is against buyers who actually SAW the step.
  const seenRef = useItemSeenBi('billing-cycle-step', {
    additionalFields: { plan_id: option.agentPlanId },
  });

  const offers = cycleOffers(option);
  const [selected, setSelected] = useState<Cadence>(() => defaultCycle(option));
  const saving = yearlySaving(option);
  const premium = monthlyPremium(option);

  const select = (cadence: Cadence) => {
    setSelected(cadence);
    // Same event the control arm's toggle fires, so the cadence choice is one query across both arms.
    // `chosen_at` is what separates them: `toggle` above the cards, `step` on this screen.
    logPageClickBi(
      { page_name: 'packages', button_name: 'select_billing_cycle' },
      {
        componentName: cadence,
        additionalFields: { plan_id: option.agentPlanId, cadence, chosen_at: 'step' },
      },
    );
  };

  // Only ever false if the catalog priced neither cadence — the picker does not enter this screen in
  // that state, so this is a guard rather than a reachable message.
  const purchasable = offers.some((o) => o.cadence === selected);

  // A coupon is validated against ONE checkout target (plan + cadence + currency), so it is dropped
  // the moment the target moves — the same rule the plan card applies when its Max/Max+ radio or the
  // cadence toggle changes. Here only the cadence can move.
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  useEffect(() => {
    setCoupon(null);
  }, [selected]);
  // Coupons ride on CreateCheckoutSession only — SwitchPlan has no coupon entry point (ADR-029), so
  // an existing subscriber is never offered the field, exactly as on the cards.
  const showCoupon = couponsEnabled && !isSwitch && purchasable;

  return (
    <div className={styles.step} ref={seenRef}>
      <TextButton
        className={styles.back}
        size="small"
        startIcon={<ArrowLeft size={16} aria-hidden className="dir-flip" />}
        onClick={() => {
          logPageClickBi({ page_name: 'packages', button_name: 'billing_cycle_back' });
          onBack();
        }}
      >
        {t('common.back')}
      </TextButton>

      <Typography variant="h1" component="h1" className={styles.title}>
        {t('pricing.billingCycle.title', { planName })}
      </Typography>

      <div className={styles.rows} role="radiogroup" aria-label={t('pricing.billingCycle.title', { planName })}>
        {offers.map((offer) => (
          <CycleRow
            key={offer.cadence}
            cadence={offer.cadence}
            label={t(offer.cadence === 'yearly' ? 'pricing.billing.yearlyPlain' : 'pricing.billing.monthly')}
            // Both rows quote a PER-MONTH figure, which is the only way the two are comparable at a
            // glance — and for yearly it is the same number the card headline showed a screen ago, so
            // the price the buyer just read is the price they meet here. The yearly row says
            // "x 12 months" rather than a bare annual total because the total is what makes people
            // hesitate while the commitment is what they are actually agreeing to.
            //
            // The annual charge is deliberately NOT stated on this screen. It briefly led this row
            // ("$1,200 / year", with the per-month breakdown beneath), and the frame now draws only
            // the breakdown — one line, both rows comparable, nothing under the head. The exact sum
            // is still shown before any money moves: Stripe Checkout states it on the next screen,
            // and the disclaimer under these rows says so.
            //
            // It is built from the ROUNDED per-month figure, so a yearly price of 1199 renders
            // "$100 x 12 months" (= 1200). That is the same arithmetic the CARD's headline does a
            // screen earlier, which is the point — the two screens must not disagree by a rounding.
            price={t(
              offer.cadence === 'yearly' ? 'pricing.billingCycle.yearlyPrice' : 'pricing.billingCycle.monthlyPrice',
              { price: formatMoney(offer.perMonth, currency) },
            )}
            badge={
              offer.cadence === 'yearly' && saving != null
                ? t('pricing.billingCycle.save', { amount: formatMoney(saving, currency) })
                : undefined
            }
            selected={selected === offer.cadence}
            onSelect={() => select(offer.cadence)}
            credits={option.credits}
            featureKeys={featureKeys}
            featuresHeading={t(
              offer.cadence === 'yearly'
                ? 'pricing.billingCycle.whatYouGet.yearly'
                : 'pricing.billingCycle.whatYouGet.monthly',
            )}
            // The monthly row argues against ITSELF, where there is a yearly price to argue against:
            // the same credits at a worse rate, and a link straight to the row that fixes it. Listing
            // what monthly "gets you" beside a cheaper row that gets you exactly the same thing was
            // saying nothing — both cadences carry one plan's benefits, so the only difference between
            // these two panels is the price of the credits.
            //
            // `undefined` in a monthly-only market (see `monthlyPremium`), where the row falls back to
            // the feature list: nothing is given up by taking the only offer on the screen.
            details={
              offer.cadence === 'monthly' && premium ? (
                <MonthlyDownside
                  premium={premium}
                  currency={currency}
                  onChooseYearly={() => {
                    // Its own button_name, not `select_billing_cycle`'s: moving a buyer off monthly is
                    // the whole reason this copy exists, and a switch made from the nudge has to be
                    // countable apart from a click on the yearly row itself.
                    logPageClickBi(
                      { page_name: 'packages', button_name: 'billing_cycle_yearly_nudge' },
                      { additionalFields: { plan_id: option.agentPlanId } },
                    );
                    select('yearly');
                  }}
                />
              ) : undefined
            }
          />
        ))}
      </div>

      {/* The promo-code field lives HERE in this arm, not on the plan cards, and that is a
          correctness requirement rather than a layout choice. `ValidateCoupon` takes a target of
          {planId, cadence, currency}, so "is this code valid" is only answerable once the cadence is
          known — and this arm removes the toggle that used to settle it on the cards. Left on a card
          it would validate against whatever cadence the card happened to render, then be carried into
          a checkout for a different one: CreateCheckoutSession re-validates and REFUSES, so the buyer
          loses the purchase, not just the discount. */}
      {/* ONE line, flush with the cards' own sides: set the price on the left, pay it on the right. */}
      <div className={styles.actions}>
        {showCoupon && (
          <div className={styles.couponField}>
            <CouponField
              planName={planName}
              planKey={planKey}
              option={option}
              cadence={selected}
              currency={currency}
              applied={coupon}
              onApply={setCoupon}
              onRemove={() => setCoupon(null)}
              disabled={busy}
            />
          </div>
        )}

        {/* The chosen cadence as a `component_route` segment. It wraps the CTA rather than the screen
            for two reasons: `clickCta` takes no route (the library composes it from this chain), and
            the cycle rows above would report the cadence they are LEAVING — the provider holds the
            value from the render the click happened in, which for those rows is the previous one. */}
        <GenericEventsRouteProvider value={selected}>
          <ContinueCta
            planKey={planKey}
            planName={planName}
            option={option}
            cadence={selected}
            isSwitch={isSwitch}
            busy={busy}
            purchasable={purchasable}
            couponCode={coupon?.code}
            onContinue={onContinue}
          />
        </GenericEventsRouteProvider>
      </div>
    </div>
  );
}

/**
 * The step's Continue, split out for ONE reason: it needs a logger of its own.
 *
 * `component_route` is read from the provider chain where the hook runs, so a logger created in the
 * parent — above the cadence provider — would report the route without the cadence in it. A child
 * with its own `useGenericBi` is the only way the segment reaches the event.
 */
function ContinueCta({
  planKey,
  planName,
  option,
  cadence,
  isSwitch,
  busy,
  purchasable,
  couponCode,
  onContinue,
}: {
  planKey: string;
  planName: string;
  option: PickerOption;
  cadence: Cadence;
  isSwitch: boolean;
  busy: boolean;
  purchasable: boolean;
  couponCode?: string;
  onContinue: (cadence: Cadence, couponCode?: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  const attribution = usePurchaseAttribution();
  const fields = {
    plan_id: option.agentPlanId,
    plan_name: planKey,
    cadence,
    credits: String(option.credits),
  };
  return (
    <div className={styles.cta}>
      <Button
        className={styles.ctaButton}
        size="large"
        loading={busy}
        disabled={!purchasable}
        onClick={() => {
          if (busy || !purchasable) return;
          // Two events, deliberately.
          //
          // `billing_cycle_continue` is this screen's own — it fires on BOTH branches, because
          // subscribers are half the population this arm routes through here and "reached the step,
          // pressed Continue" has to be countable for them too.
          logPageClickBi(
            { page_name: 'packages', button_name: 'billing_cycle_continue' },
            { componentName: planKey, additionalFields: { ...fields, is_switch: String(isSwitch) } },
          );
          // `buy_plan` is the funnel's existing purchase step. It used to be fired by `buyPlan` in
          // packages.tsx, which knew only an id; now it is fired where the plan and the cadence both
          // are. Checkout branch only — a switch was never a `buy_plan`, and counting it as one would
          // change what the existing funnel measures.
          if (!isSwitch) {
            logPageClickBi(
              { page_name: 'packages', button_name: 'buy_plan' },
              // `origin` / `feature` on `buy_plan` only (docs/compliance/bi.md §2), the same pair the card's
              // buy_plan carries on the control arm.
              { componentName: planKey, additionalFields: { ...fields, origin: attribution.origin, feature: attribution.feature } },
            );
          }
          onContinue(cadence, couponCode);
        }}
      >
        {isSwitch ? t('pricing.cta.switchTo', { planName }) : t('pricing.billingCycle.continue')}
      </Button>
    </div>
  );
}

/**
 * One cycle option.
 *
 * A real `<input type="radio">` under a `<label>`, visually replaced by the DS `SelectionIndicator` —
 * the bespoke-layout case that mark is exported for. The two rows are a single choice, and a radio
 * group is what gives a keyboard user arrow-key selection and a screen reader "1 of 2" without any of
 * it being rebuilt here by hand. The whole card is the target: on a two-option screen anything smaller
 * is a needless miss.
 */
function CycleRow({
  cadence,
  label,
  price,
  badge,
  selected,
  onSelect,
  credits,
  featureKeys,
  featuresHeading,
  details,
}: {
  cadence: Cadence;
  label: ReactNode;
  price: ReactNode;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
  credits: number;
  featureKeys: string[];
  featuresHeading: string;
  /** Replaces the "what you get" list under the credits rule. The monthly row's own case. */
  details?: ReactNode;
}) {
  const { t } = useTranslation();
  const Chevron = selected ? ChevronUp : ChevronDown;
  return (
    <Card className={`${styles.row}${selected ? ` ${styles.rowSelected}` : ''}`} interactive="hover">
      {/* The <label> wraps the HEAD ONLY, not the card.
          A radio's accessible name is its label's whole text content, so a label around the whole
          card made the selected row announce itself as "Yearly, $100 x 12 months, Save $300, 12,000
          monthly credits, What you get with the yearly plan, All your agents at full throttle, Deep
          agent activity across every tool, ..." — the entire expanded panel read out as the name of
          the control. The details are describing the row, not naming it. Collapsed, the head
          IS the whole visible card, so the hit target is unchanged for the row you are being asked
          to pick; the expanded panel belongs to the row already chosen and has nothing left to
          select. */}
      <label className={styles.head}>
        <input
          type="radio"
          name="billing-cycle"
          value={cadence}
          checked={selected}
          onChange={onSelect}
          className={styles.input}
        />
        <SelectionIndicator checked={selected} className={styles.indicator} />
        <Typography variant="h3" component="span" className={styles.label}>
          {label}
        </Typography>
        <span aria-hidden="true" className={styles.dash}>
          -
        </span>
        <Typography variant="p2" color="secondary" component="span" className={styles.price}>
          {price}
        </Typography>
        <span className={styles.trailing}>
          {/* The DS Badge at MEDIUM — the frame's own component and size (a `Badge Symphony DS`
              instance, 76x28). The className carries only the solid blue fill; everything else is
              the design system's. */}
          {badge && <Badge size="medium" className={styles.save} label={badge} />}
          <Chevron size={16} aria-hidden className={styles.chevron} />
        </span>
      </label>

      {selected && (
        <div className={styles.details}>
          <Typography variant="p3" component="span" className={styles.credits}>
            <Database size={14} aria-hidden className={styles.creditsIcon} />
            {t('pricing.monthlyCredits', { credits: credits.toLocaleString() })}
          </Typography>
          <hr className={styles.rule} />
          {details ?? (
            <>
              <Typography variant="h4" component="div" className={styles.featureHeading}>
                {featuresHeading}
              </Typography>
              <ul className={styles.features}>
                {featureKeys.map((key) => (
                  <li key={key} className={styles.feature}>
                    <Check size={16} aria-hidden className={styles.check} />
                    <Typography variant="p2" component="span">
                      {t(key)}
                    </Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * What the monthly row costs you, in the monthly row.
 *
 * The plan is identical on both cadences — same credits, same features — so the only honest thing this
 * panel can list is the price of those credits, twice: once as the percentage, once as the rate the
 * percentage is a ratio of. The two lines are the same fact stated at two altitudes on purpose; the
 * percentage is what a buyer remembers and the rate is what makes it checkable.
 *
 * The last line is a real control, not a sentence about one. It selects the yearly row above rather
 * than navigating anywhere, so the fix for what the panel just said is one click away and lands the
 * buyer on the offer they were already looking at.
 */
function MonthlyDownside({
  premium,
  currency,
  onChooseYearly,
}: {
  premium: MonthlyPremium;
  currency?: string;
  onChooseYearly: () => void;
}) {
  const { t } = useTranslation();
  const lines = [
    t('pricing.billingCycle.monthlyMissing.premium', { percent: premium.percent }),
    t('pricing.billingCycle.monthlyMissing.rate', {
      monthly: formatRate(premium.monthlyPerThousand, currency),
      yearly: formatRate(premium.yearlyPerThousand, currency),
    }),
  ];
  // The link is ONE word pair inside a sentence, so the sentence arrives as a template with a
  // `{link}` placeholder and is split around it — the repo idiom (`BasedOnProvider` in
  // personality/VoiceSection). Splitting rather than concatenating is what lets a translator put the
  // link wherever their language wants it instead of forcing English word order.
  const nudge = t('pricing.billingCycle.monthlyMissing.switch');
  const [before, ...after] = nudge.split('{link}');

  return (
    <>
      <Typography variant="h4" component="div" className={styles.featureHeading}>
        {t('pricing.billingCycle.monthlyMissing.heading')}
      </Typography>
      <ul className={styles.features}>
        {lines.map((line) => (
          <li key={line} className={styles.feature}>
            <X size={16} aria-hidden className={styles.cross} />
            <Typography variant="p2" component="span">
              {line}
            </Typography>
          </li>
        ))}
      </ul>
      <Typography variant="p2" component="div" className={styles.nudge}>
        {after.length === 0 ? (
          nudge
        ) : (
          <>
            {before}
            <TextButton size="small" underline onClick={onChooseYearly}>
              {t('pricing.billingCycle.monthlyMissing.switchLink')}
            </TextButton>
            {after.join(t('pricing.billingCycle.monthlyMissing.switchLink'))}
          </>
        )}
      </Typography>
    </>
  );
}
